import { type CSSProperties, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetEnterpriseThermostatsQuery } from "store/api/enterpriseApi"
import { useGetEnterpriseDashboardThermostatWidgetsQuery } from "store/api/widgetsApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setSelectedDeviceIDs } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import DevicePhysicalStatus from "types/enums/DevicePhysicalStatus"

import { parseDateField } from "utils/dateUtils"
import { formatSystemAge } from "utils/formatSystemAge"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { TransferModal } from "modals/TransferModal"
import { WidgetLayout } from "layouts/WidgetLayout"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { RainbowChart } from "components/common/RainbowChart"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import TableWrapper from "components/table/TableWrapper"
import { WidgetPhysicalStatus } from "components/widgets/WidgetPhysicalStatus"

import InventoryConfirmationModal from "../../contractor/ContractorDevicesPage/InventoryConfirmationModal"
import PreinstalledConfirmationModal from "../../contractor/ContractorDevicesPage/PreInstalledConfirmationModal"
import WarrantyConfirmationModal from "../../contractor/ContractorDevicesPage/WarrantyConfirmationModal"
import { enterpriseDevicesTableHeaderCells } from "./enterpriseDevicesTableHeaderCells"
import { EnterpriseMissingModal, EnterpriseReturnFromMissingModal } from "./EnterpriseStatusModals"

import styles from "./index.module.scss"

const TABLE_NAME = "admin_devices_list"

const InventoryWidget = ({ widget }: { widget?: ApiTypes.Res.InventoryWidget }) => {
  const thermv2ordering = useAppSelector(isFeatureEnabled(FeatureFlag.ThermV2Ordering))
  const inventoryV1 = widget?.inventory_devices_v1 ?? 0
  const inventoryV2 = widget?.inventory_devices_v2 ?? 0
  const inventory = inventoryV1 + inventoryV2
  const ordered = widget?.ordered_devices ?? 0
  const total = inventory + ordered
  const percentOf = (value: number) => (total > 0 ? `${(value / total) * 100}%` : "0%")

  return (
    <WidgetLayout title="Inventory" className={classNames(styles.summaryWidget)}>
      <div className={styles.summaryWidgetInner}>
        <div className={styles.inventoryBarWrap}>
          <div className={styles.inventoryBar}>
            {thermv2ordering ? (
              <>
                <span className={styles.inventoryBarInStock} style={{ flexBasis: percentOf(inventoryV1) }} />
                <span className={styles.inventoryBarInStockV2} style={{ flexBasis: percentOf(inventoryV2) }} />
              </>
            ) : (
              <span className={styles.inventoryBarInStock} style={{ flexBasis: percentOf(inventory) }} />
            )}
            <span className={styles.inventoryBarOrdered} style={{ flexBasis: percentOf(ordered) }} />
          </div>
          <div className={styles.inventorySufficientFor}>
            <span>Sufficient for</span>
            <div className={styles.inventoryDays}>
              {widget?.sufficient_days ?? 0}
              <small>Days</small>
            </div>
          </div>
        </div>
        <div className={styles.inventoryLegendRow}>
          {thermv2ordering ? (
            <>
              <div className={styles.inventoryLegendItem}>
                <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#3b82f6" } as CSSProperties}>
                  In Inventory V1
                </span>
                <strong>{inventoryV1}</strong>
              </div>
              <div className={styles.inventoryLegendItem}>
                <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#1e3a8a" } as CSSProperties}>
                  In Inventory V2
                </span>
                <strong>{inventoryV2}</strong>
              </div>
            </>
          ) : (
            <div className={styles.inventoryLegendItem}>
              <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#3b82f6" } as CSSProperties}>
                In Inventory
              </span>
              <strong>{inventory}</strong>
            </div>
          )}
          <div className={styles.inventoryLegendItem}>
            <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#dbeafe" } as CSSProperties}>
              Ordered
            </span>
            <strong>{ordered}</strong>
          </div>
        </div>
      </div>
    </WidgetLayout>
  )
}

const MonitoringWidget = ({ widget }: { widget?: ApiTypes.Res.MonitoringWidget }) => {
  const purchased = widget?.purchased_devices ?? 0
  const installed = widget?.installed_devices ?? 0
  const remaining = Math.max(purchased - installed, 0)
  const percent = purchased > 0 ? Math.round((installed / purchased) * 100) : 0

  return (
    <WidgetLayout title="Monitoring" className={classNames(styles.summaryWidget)}>
      <div className={styles.monitoringChartWrap}>
        <RainbowChart
          values={[installed, remaining]}
          colors={["#48d7f3", "#e2e8f0"]}
          options={{ cutout: "68%", rotation: -90, circumference: 180 }}
        />
        <div className={styles.monitoringMainValue}>
          <span>INSTALLED</span>
          <strong>{percent}%</strong>
        </div>
      </div>
      <div className={styles.inventoryLegendRow}>
        <div className={styles.inventoryLegendItem}>
          <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#48d7f3" } as CSSProperties}>
            Purchased
          </span>
          <strong>{purchased}</strong>
        </div>
        <div className={styles.inventoryLegendItem}>
          <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#e2e8f0" } as CSSProperties}>
            Installed
          </span>
          <strong>{installed}</strong>
        </div>
      </div>
    </WidgetLayout>
  )
}

const convertEnterpriseDevicesToTableRows = (data?: ApiTypes.Model.EnterpriseThermostat[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    device =>
      ({
        checkbox: {},
        sn: { value: device.sn },
        work_status: { value: device.work_status },
        contractor_name: { value: device.contractor },
        phisical_status: { value: device.physical_status },
        activation_date: { value: parseDateField(device.activation_date) },
        red_flag: { value: `${device.red_flag ? "Yes" : "No"}` },
        red_flag_type: { value: device.red_flag_type },
        homeowner: { value: device.homeowner },
        email: { value: device.email },
        country: { value: device.country_name },
        zip_code: { value: device.zip_code },
        address: { value: device.address },
        phone: { value: device.phone },
        system_age: { value: formatSystemAge(device.system_age), csvValue: formatSystemAge(device.system_age) },
        device_location: { value: device.device_location },
        rowSettings: {
          id: Number(device.id),
          isRedFlag: device.red_flag,
          item: {
            ...device,
            phisical_status: device.physical_status,
            contractor_name: device.contractor,
          },
        },
      }) as UITypes.TableBodyRow,
  )
}

export const EnterpriseDevicesPage = () => {
  const [triggerGetDevices, { isFetching, data }] = useLazyGetEnterpriseThermostatsQuery()
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const { data: deviceWidgetsResponse } = useGetEnterpriseDashboardThermostatWidgetsQuery()
  const { urlQueryParams } = useUrlQueryParams()
  const dispatch = useAppDispatch()
  const selectedDeviceIDs = useAppSelector(state => state.tempData.selectedDeviceIDs)
  const [showInventoryModal, setShowInventoryModal] = useState(false)
  const [showPreInstalledModal, setShowPreInstalledModal] = useState(false)
  const [showWarrantyModal, setShowWarrantyModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showMissingModal, setShowMissingModal] = useState(false)
  const [showReturnFromMissingModal, setShowReturnFromMissingModal] = useState(false)
  const devices = data?.data ?? []
  const deviceWidgets = deviceWidgetsResponse?.data

  const tableHeaderCells = useMemo((): UITypes.TableHeaderCell[] => {
    return columnsData?.data?.config
      ? reorderTableHeaderCells(enterpriseDevicesTableHeaderCells, columnsData.data.config)
      : enterpriseDevicesTableHeaderCells
  }, [columnsData])

  const tableRows = useMemo(() => convertEnterpriseDevicesToTableRows(devices), [devices])
  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta: data?.meta,
    fileName: "enterprise-devices",
    convertData: convertEnterpriseDevicesToTableRows,
    useLazyQuery: useLazyGetEnterpriseThermostatsQuery,
  })

  const selectedPageDevices = useMemo(
    () => devices.filter(device => selectedDeviceIDs.includes(device.id)),
    [devices, selectedDeviceIDs],
  )

  const onDeviceSelect = (id: number) => {
    dispatch(
      setSelectedDeviceIDs(
        selectedDeviceIDs.includes(id) ? selectedDeviceIDs.filter(item => item !== id) : [...selectedDeviceIDs, id],
      ),
    )
  }

  const toggleAllDevices = () => {
    const currentPageDeviceIDs = devices.map(device => device.id)
    const allSelected = currentPageDeviceIDs.every(id => selectedDeviceIDs.includes(id))
    dispatch(
      setSelectedDeviceIDs(
        allSelected
          ? selectedDeviceIDs.filter(id => !currentPageDeviceIDs.includes(id))
          : [...new Set([...selectedDeviceIDs, ...currentPageDeviceIDs])],
      ),
    )
  }

  const selectedStatuses = selectedPageDevices.map(device => device.physical_status)
  const hasAnyMissingDevice = selectedStatuses.includes(DevicePhysicalStatus.MISSING)
  const allDevicesAreMissing =
    selectedStatuses.length > 0 && selectedStatuses.every(status => status === DevicePhysicalStatus.MISSING)
  const canMarkMissing =
    selectedPageDevices.length > 0 &&
    (hasAnyMissingDevice
      ? allDevicesAreMissing
      : selectedStatuses.every(
          status => !["installed", "transferring", "preinstalled", "missing", "warranty"].includes(status),
        ))
  const canTransfer =
    selectedPageDevices.length > 0 && !selectedStatuses.some(status => ["damaged", "transferring"].includes(status))
  const canMarkWarranty =
    selectedPageDevices.length > 0 &&
    selectedStatuses.every(status => ["preinstalled", "installed", "inventory"].includes(status))
  const canMoveToInventory =
    selectedPageDevices.length > 0 && selectedStatuses.every(status => status === DevicePhysicalStatus.PREINSTALLED)
  const canMoveToPreInstalled =
    selectedPageDevices.length > 0 && selectedStatuses.every(status => status === DevicePhysicalStatus.INVENTORY)

  const handleCancel = () => {
    setShowInventoryModal(false)
    setShowPreInstalledModal(false)
    setShowWarrantyModal(false)
    setShowTransferModal(false)
    setShowMissingModal(false)
    setShowReturnFromMissingModal(false)
  }

  const handleSuccess = () => {
    handleCancel()
    dispatch(setSelectedDeviceIDs([]))
    sendRequest(urlQueryParams)
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetDevices(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    sendRequest(urlQueryParams)
  }, [urlQueryParams])

  useEffect(() => {
    triggerGetTableColumns({ list_name: TABLE_NAME })
  }, [triggerGetTableColumns])

  useEffect(
    () => () => {
      dispatch(setSelectedDeviceIDs([]))
    },
    [dispatch],
  )

  return (
    <>
      <section className={styles.widgetsSection}>
        <div className={styles.widgetsWrapper}>
          <WidgetPhysicalStatus variant="enterprise" />
          <InventoryWidget widget={deviceWidgets?.inventory} />
          <MonitoringWidget widget={deviceWidgets?.monitoring} />
        </div>
      </section>
      <ContentWrapper
        title="Thermostats"
        renderHeaderAccessoryView={() => (
          <>
            <div className={styles.tableActions}>
              <EditColumnsButton tableName={TABLE_NAME} tableHeaderCells={tableHeaderCells} />
              <ButtonNew onClick={handleExport} disabled={data?.meta?.total === 0}>
                Export
              </ButtonNew>
            </div>
          </>
        )}
      >
        <nav className={styles.tableNavWrapper}>
          <div className={styles.statusPills}>
            <button
              type="button"
              className={classNames(styles.statusPill, styles.missing)}
              disabled={!canMarkMissing}
              onClick={() => (hasAnyMissingDevice ? setShowReturnFromMissingModal(true) : setShowMissingModal(true))}
            >
              {hasAnyMissingDevice ? "Return from Missing" : "Missing"}
            </button>
            <button
              type="button"
              className={classNames(styles.statusPill, styles.transfer)}
              disabled={!canTransfer}
              onClick={() => setShowTransferModal(true)}
            >
              Transfer
            </button>
            <button
              type="button"
              className={classNames(styles.statusPill, styles.warranty)}
              disabled={!canMarkWarranty}
              onClick={() => setShowWarrantyModal(true)}
            >
              Warranty
            </button>
            <button
              type="button"
              className={classNames(styles.statusPill, styles.inventory)}
              disabled={!canMoveToInventory}
              onClick={() => setShowInventoryModal(true)}
            >
              In Inventory
            </button>
            <button
              type="button"
              className={classNames(styles.statusPill, styles.preinstalled)}
              disabled={!canMoveToPreInstalled}
              onClick={() => setShowPreInstalledModal(true)}
            >
              Pre-Installed
            </button>
          </div>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!!columnsData}
          isLoading={isFetching}
          currentItemIDs={selectedDeviceIDs}
          onCheckboxChange={onDeviceSelect}
          onHeaderCheckboxChange={toggleAllDevices}
          isHeaderChecked={devices.length > 0 && devices.every(device => selectedDeviceIDs.includes(device.id))}
          data={tableRows}
          additionalTags={
            selectedPageDevices.length ? [{ label: "S/N", values: selectedPageDevices.map(item => item.sn) }] : []
          }
          clearAdditionalTags={() => dispatch(setSelectedDeviceIDs([]))}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {showInventoryModal && (
        <InventoryConfirmationModal
          selectedDevices={selectedPageDevices}
          onCancel={handleCancel}
          onSuccess={handleSuccess}
        />
      )}
      {showPreInstalledModal && (
        <PreinstalledConfirmationModal
          selectedDevices={selectedPageDevices}
          onCancel={handleCancel}
          onSuccess={handleSuccess}
        />
      )}
      {showWarrantyModal && (
        <WarrantyConfirmationModal
          selectedDevices={selectedPageDevices}
          onCancel={handleCancel}
          onSuccess={handleSuccess}
        />
      )}
      {showTransferModal && (
        <TransferModal
          selectedDeviceIDs={selectedDeviceIDs}
          onCancel={handleCancel}
          onSuccess={handleSuccess}
          skipVendor
        />
      )}
      {showMissingModal && <EnterpriseMissingModal onCancel={handleCancel} onSuccess={handleSuccess} />}
      {showReturnFromMissingModal && (
        <EnterpriseReturnFromMissingModal onCancel={handleCancel} onSuccess={handleSuccess} />
      )}
    </>
  )
}

export default EnterpriseDevicesPage
